<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.invoicing',
    path: 'invoicing',
    component: 'SettingsInvoicing',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'SettingsInvoicing',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const updateItem = (...args) => {
    const [data, extras] = args;

    return $fn.value
      .crud('invoicing_settings')
      .update(
        _.extend(
          _.pick(data, [
            'company',
            'draft_numbering',
            'numbering',
            'invoicing_mode',
            'invoicing_provider',
            'template',
          ]),
          extras
        ),
        {
          parameters: { company_id: data.company.id },
        }
      );
  };
  const onSave = (...args) => {
    const [$event] = args;
    const row = $event.data.row;

    return $event.data.waitFor(updateItem(row));
  };
  const configureNumbering = (...args) => {
    const [value, row] = args;

    return $imodal.value.show({
      name: 'InvoicingNumbering',
      props: {
        company: row.company,
        oid: value ? value.id : null,
        saving(numbering) {
          return updateItem(row, { numbering });
        },
      },
    });
  };
  const configureProvider = (...args) => {
    const [value, row] = args;

    return $imodal.value
      .show({
        name: 'InvoicingProviderSettings',
        props: {
          item: _.cloneDeep(value),
          saving(invoicing_provider) {
            return updateItem(row, { invoicing_provider });
          },
        },
      })
      .then((result) => {
        if (result) {
          loadData('items');
        }
      });
  };

  const items = ref(null);
  const numberings = ref(null);
  const templates = ref(null);
  const invoicingModes = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'company',
      field: 'company.name',
      header: $fn.value.pgettext('Settings|TableHeader', 'Company'),
      width: '220px',
      editable: false,
    },
    {
      name: 'invoicing_mode',
      header: $fn.value.pgettext('Settings|TableHeader', 'Invoicing mode'),
      width: '160px',
      type: 'select2',
      options: invoicingModes.value,
      valueField: 'value',
      textField: 'text',
    },
    {
      name: 'draft_numbering',
      header: $fn.value.pgettext('Settings|TableHeader', 'Draft numbering'),
      width: '260px',
      type: 'select2',
      options: numberings.value,
      textField: 'description',
      path: 'description',
      bindObject: true,
      clearable: true,
    },
    {
      name: 'numbering',
      header: $fn.value.pgettext('Settings|TableHeader', 'Numbering'),
      width: '260px',
      editable: false,
      formatter: {
        name: 'template',
        value:
          '<div>\n\
  <div\n\
    v-if="value && value.length"\n\
    class="d-flex flex-column"\n\
  >\n\
    <div\n\
      v-for="item in value"\n\
      :key="item.id"\n\
    >\n\
      <ItpButton\n\
        v-if="item.is_default"\n\
        variant="light"\n\
        @click.prevent.stop="globs.configureNumbering(item, row)"\n\
      >\n\
        <ItpIcon icon="icon-park-outline:dot"></ItpIcon>\n\
        {{ item.description }}\n\
      </ItpButton>\n\
\n\
      <BDropdown\n\
        v-if="!item.is_default"\n\
        variant="light"\n\
        split\n\
        split-class="border"\n\
        toggle-class="border"\n\
        @click.prevent.stop="globs.configureNumbering(item, row)"\n\
      >\n\
        <template slot="button-content">\n\
          {{ item.description }}\n\
        </template>\n\
\n\
        <BDropdownItem\n\
          @click.prevent.stop="globs.setAsDefaultNumbering(item, row)"\n\
        >\n\
        Set as default\n\
        </BDropdownItem>\n\
      </BDropdown>\n\
    </div>\n\
\n\
    <a class="text-nowrap me-auto"\n\
        href=""\n\
       @click.prevent.stop="globs.configureNumbering(null, row)"\n\
    >\n\
      new...\n\
    </a>\n\
  </div>\n\
</div>\n\
',
      },
    },
    {
      name: 'invoicing_provider',
      header: $fn.value.pgettext('Settings|TableHeader', 'Provider settings'),
      width: '160px',
      editable: false,
      formatter: {
        name: 'template',
        value:
          '<a href="" @click.prevent.stop="globs.configureProvider(value, row)">\n\
  <span class="text-nowrap" v-if="value">\n\
    {{ value.name }}\n\
  </span>\n\
  <span class="text-nowrap" v-if="!value">\n\
    unset\n\
  </span>\n\
</a>\n\
',
      },
    },
    {
      name: 'template',
      header: $fn.value.pgettext('Settings|TableHeader', 'Template'),
      width: '160px',
      type: 'select2',
      options: templates.value,
      path: 'name',
      textField: 'name',
      bindObject: true,
      clearable: true,
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    numberings: numberings.value,
    templates: templates.value,
    invoicingModes: invoicingModes.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = async () => {
    return $fn.value
      .fetch('list_invoicing_settings')
      .then((data) =>
        _.orderBy(data, ['company.name']).map((p) =>
          _.extend(p, { id: p.company.id })
        )
      );
  };
  const $$load_numberings = async () => {
    return $fn.value
      .fetch('list_numbering')
      .then((data) => _.filter(data, { resource_type: 'invoice:out' }));
  };
  const $$load_templates = async () => {
    return $fn.value
      .fetch('list_template')
      .then((data) =>
        _.filter(data, { template_type: 'html', object_type: 'invoice' })
      );
  };
  const $$load_invoicingModes = async () => {
    return $fn.value.getEnumValuesFromSchema('InvoicingMode');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'numberings' },
      { name: 'templates' },
      { name: 'invoicingModes' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_3343864469950044_columns',
        nodeId: '3343864469950044',
        dependsOn: ['numberings', 'templates', 'invoicingModes'],
      },
    ],
    loaders: {
      items: $$load_items,
      numberings: $$load_numberings,
      templates: $$load_templates,
      invoicingModes: $$load_invoicingModes,
    },
    ux: {},
    dataRefs: {
      items,
      numberings,
      templates,
      invoicingModes,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ncui7vfsm_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_3343864469950044_script_grvqc925t_value_callable = async (
    $event
  ) => {
    return onSave($event);
  };
  const act_3343864469950044_script_grvqc925t_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_grvqc925t_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_ncui7vfsm =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ncui7vfsm_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ehv5ni6jr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ncui7vfsm,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ehv5ni6jr],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_mdhvxjfc6 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_nrfm3ejax =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_mdhvxjfc6,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_nrfm3ejax],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_n0fc7t9pa =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_c7xp5540x =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_n0fc7t9pa,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_c7xp5540x],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_grvqc925t = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_grvqc925t_getActionArgs,
  });
  const inst_evh_3343864469950044_save_xwgulgngg =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_grvqc925t,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_save_xwgulgngg],
    event: 'save',
  });

  const actions = {
    inst_evh_7315092382398562_close_ehv5ni6jr,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_nrfm3ejax,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_c7xp5540x,
    reload: inst_evh_8320016629450276_clickPreventStop_c7xp5540x,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_3343864469950044_save_xwgulgngg,
    inst_evh_3343864469950044_save,
  };

  const evh_7315092382398562_close_ehv5ni6jr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ehv5ni6jr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_nrfm3ejax = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_nrfm3ejax.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_c7xp5540x = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_c7xp5540x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save_xwgulgngg = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save_xwgulgngg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="SettingsInvoicing"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox direction="column" class="p-3" v-if="loaded">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{ pgettext('Settings', 'Invoicing configuration') }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.Invoicing"
        editable
        :isRowEditable="(p) => !p.locked"
        :value="items"
        no-create-row
        no-delete-row
        :globs="{
          configureNumbering,
          configureProvider,
          setAsDefaultNumbering,
        }"
        :columns="b_3343864469950044_columns"
        class="h-100"
        ref="table"
        v-if="loaded"
        @save="evh_3343864469950044_save($event, {})"
      >
      </ItpDataTable>
    </ItpBox>
  </ItpPage>
</template>
